import React, { useState, useEffect } from 'react';
import './Reservation.css';

import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaPhoneAlt, FaCalendar, FaClock, FaUsers, FaComment } from 'react-icons/fa';


const RESERVATION_EMAIL = 'the23coffeeshop@yahoo.com';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;


const Reservation = () => {  

    // State to manage form data
      const [formData, setFormData] = useState({
        name: '',
        email: '',
        number: '',
        guests: '',
        date: '',
        time: '',
        comment: ''
      });
    
      const [mailSent, setMailSent] = useState(false);
      const [error, setError] = useState('');
      const [isSubmitting, setIsSubmitting] = useState(false);
      const [emailLink, setEmailLink] = useState('');
    
      useEffect(() => {
        if (mailSent) {
          const timer = setTimeout(() => setMailSent(false), 3000);
          return () => clearTimeout(timer);
        }
      }, [mailSent]);
    
      const buildReservationEmail = () => {
        const subject = `Table Reservation Request - ${formData.date} at ${formData.time}`;
        const body = [
          'Table Reservation Request',
          '',
          `Name: ${formData.name}`,
          `Email: ${formData.email}`,
          `Phone: ${formData.number}`,
          `Guests: ${formData.guests}`,
          `Date: ${formData.date}`,
          `Time: ${formData.time}`,
          '',
          'Message:',
          formData.comment,
        ].join('\n');

        return `mailto:${RESERVATION_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      };

      const openEmailDraft = (href) => {
        const emailWindow = window.open(href, '_blank', 'noopener,noreferrer');

        if (emailWindow) {
          return;
        }

        const emailAnchor = document.createElement('a');
        emailAnchor.href = href;
        emailAnchor.target = '_blank';
        emailAnchor.rel = 'noopener noreferrer';
        emailAnchor.style.display = 'none';
        document.body.appendChild(emailAnchor);
        emailAnchor.click();
        document.body.removeChild(emailAnchor);
      };

      const handleSubmitForm = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
    
        try {
          const validationErrors = [];
    
          if (!formData.name.trim()) validationErrors.push('Name is required');
          if (!formData.email.trim()) {
            validationErrors.push('Email is required');
          } else if (!EMAIL_REGEX.test(formData.email)) {
            validationErrors.push('Invalid email format');
          }
          if (!formData.number.trim()) {
            validationErrors.push('Phone number is required');
          } else if (!PHONE_REGEX.test(formData.number)) {
            validationErrors.push('Invalid phone number format');
          }
          if (!formData.guests) validationErrors.push('Number of guests is required');
          if (!formData.date.trim()) validationErrors.push('Date is required');
          if (!formData.time.trim()) validationErrors.push('Time is required');
          if (!formData.comment.trim()) validationErrors.push('Comment is required');
    
          if (validationErrors.length > 0) {
            throw new Error(validationErrors.join(' | '));
          }
    
          const reservationEmailLink = buildReservationEmail();
          setEmailLink(reservationEmailLink);
          openEmailDraft(reservationEmailLink);
    
          setMailSent(true);
        } catch (err) {
          setError(err.message || 'An error occurred');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
      };


  return (   
    <>
      <PageTitle title={'reservation'} page={'reservation'} /> 
      <section className='reservation'>

        <div className="box-container">

          {/* Embedded Google Map */}
          <iframe 
            src="https://www.google.com/maps?q=2667%20Powder%20Springs%20Rd%20SW%20Ste%20105%2C%20Marietta%2C%20GA%2030064&output=embed" 
            allowFullScreen 
            loading="lazy"
            title="Google Maps Location"
          ></iframe>

          {/* Reservation Form */}
          <form className='dark-form' onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Table Reservation"} linear sideHeading />

            <div className='input-box'>

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input type="text" name="name" className="box" id="name" 
                  value={formData.name} onChange={handleInputChange}
                  placeholder="Name" disabled={isSubmitting} required />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input type="email" name="email" className="box" id="email" 
                  value={formData.email} onChange={handleInputChange}
                  placeholder="Email" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhoneAlt /></label>
                <input type="number" name="number" className="box" id="number" 
                value={formData.number} onChange={handleInputChange}
                placeholder="Phone Number" disabled={isSubmitting} required />
              </div>

              {/* Number of Guests Input Field */}
              <div className="input-field">
                <label htmlFor="guests"><FaUsers /></label>
                <input type="tel" name="guests" className="box" id="guests"
                    value={formData.guests} onChange={handleInputChange}
                    min="1" max="100" placeholder="Number Of Guests" disabled={isSubmitting} required />
              </div>

            </div>

            <div className='input-box'>

              {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input type="date" name="date" id="date" className="box" 
                  value={formData.date} onChange={handleInputChange}
                  aria-label="Reservation date" disabled={isSubmitting} required />
              </div>

              {/* Time Input Field */}
              <div className="input-field">
                <label htmlFor="time"><FaClock /></label>
                <input type="time" name="time" id="time" className="box" 
                  value={formData.time} onChange={handleInputChange}
                  aria-label="Reservation time" disabled={isSubmitting} required />
              </div>

            </div>

            {/* Comment Input Field */}
            <div className='input-box'>
              <div className="input-field text-area">
                <label htmlFor="comment"><FaComment /></label>
                <textarea cols="30" rows="10" name="comment" className="box" id="comment" 
                value={formData.comment} onChange={handleInputChange}
                placeholder="Message" disabled={isSubmitting} required></textarea>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Booking...' : 'Book Table'}
            </button>

            {/* Error and Success Messages */}
            {error && <div className="alert error">{error.split(' | ').map((msg, i) => (<span key={i}>{msg}</span>))}</div>}
            {mailSent && (
              <div className="alert success">
                <span>
                  Email draft opened with your reservation details. If it did not open, use the link below.
                </span>
                {emailLink && (
                  <a href={emailLink} className="btn" target="_blank" rel="noopener noreferrer">
                    Open email draft
                  </a>
                )}
              </div>
            )}

          </form>

        </div>

      </section> 
    </>

  )
}

export default Reservation;
